import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AdmissionGroup } from '@domain/admission-group';
import { EventSection } from '@domain/event-section';
import { MOVE_OFFER_TYPE, moveOffer, MoveOfferRequest } from '@hooks/admissions/use-move-offer';
import { createEventGroupsKey } from '@hooks/events/use-get-admission-groups';
import { createEventSectionsKey } from '@hooks/events/use-get-event-sections';
import { invalidateEventQueries } from '@hooks/events/utils/invalidate-event-queries';
import { useGenericMutation } from '@hooks/use-generic-mutation';
import { useToast } from '@hooks/use-toast/use-toast';
import { useEventUIStore } from '@stores/event-ui-store';
import { useQueryClient } from '@tanstack/react-query';
import { RequestError } from '@utils/request/request.type';
import { updateOfferPosition } from '@utils/update-offer-position/update-offer-position';

import { QUERY_KEYS } from './constants';

interface MoveOfferToGroupRequest {
  offerId: string;
  fromGroupId: string;
  toGroupId: string;
  toGroupName: string;
}

interface CacheSnapshot {
  sectionsKey: readonly unknown[];
  groupsKey: readonly unknown[];
  previousSections: EventSection[] | undefined;
  previousGroups: AdmissionGroup[] | undefined;
}

const buildMoveBody = (fromGroupId: string, toGroupId: string): MoveOfferRequest => ({
  from: { type: MOVE_OFFER_TYPE.OFFER_GROUP, id: fromGroupId },
  to: { type: MOVE_OFFER_TYPE.OFFER_GROUP, id: toGroupId },
  toPosition: 1,
});

export const useMoveOfferToGroup = ({ eventId, offerName }: { eventId?: string; offerName?: string }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const snapshotRef = useRef<CacheSnapshot | null>(null);

  return useGenericMutation<unknown, RequestError, MoveOfferToGroupRequest>(
    [QUERY_KEYS.moveOfferToGroup],
    async ({ offerId, fromGroupId, toGroupId }) => moveOffer(offerId, buildMoveBody(fromGroupId, toGroupId)),
    {
      onMutate: ({ offerId, fromGroupId, toGroupId }) => {
        if (!eventId) return;

        const sectionsKey = createEventSectionsKey(eventId);
        const groupsKey = createEventGroupsKey(eventId);

        snapshotRef.current = {
          sectionsKey,
          groupsKey,
          previousSections: queryClient.getQueryData<EventSection[]>(sectionsKey),
          previousGroups: queryClient.getQueryData<AdmissionGroup[]>(groupsKey),
        };

        updateOfferPosition(queryClient, eventId, offerId, buildMoveBody(fromGroupId, toGroupId));
        useEventUIStore.getState().setForceOpenGroupId(toGroupId);
        useEventUIStore.getState().setScrollTargetId(offerId);
      },
      onSuccess: (_data, { toGroupName }) => {
        snapshotRef.current = null;
        toast.push({
          title: t('admissionGroups:moveToGroupSuccessMessage', {
            admissionName: offerName,
            groupName: toGroupName,
          }),
          text: '',
          type: 'success',
        });

        if (eventId) {
          invalidateEventQueries(queryClient, eventId);
        }
      },
      onError: () => {
        if (snapshotRef.current) {
          queryClient.setQueryData(snapshotRef.current.sectionsKey, snapshotRef.current.previousSections);
          queryClient.setQueryData(snapshotRef.current.groupsKey, snapshotRef.current.previousGroups);
          snapshotRef.current = null;
        }
        if (eventId) {
          invalidateEventQueries(queryClient, eventId);
        }
      },
      meta: {
        hideLoader: true,
      },
    }
  );
};
